'use client';

import { isBefore } from 'date-fns';
import { useState } from 'react';
import { useTradeContext } from '../../../../components/ui/TradePageClient/TradePageClient';
import WChanceStaging from '../../../../components/ui/WChanceStaging/WChanceStaging';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import { newDate } from '../../../../utils/date';
import type { LotteryOptionGoodsForm } from '../../types';
import { createStagingInfo } from '../../utils';
import OptionNamedCollectionList from '../OptionNamedCollectionList/OptionNamedCollectionList';
import OptionNamedSelectableStepNamedCollectionList from '../OptionNamedSelectableStepNamedCollectionList/OptionNamedSelectableStepNamedCollectionList';
import OptionNamedSelectableStepQuantityCollectionList from '../OptionNamedSelectableStepQuantityCollectionList/OptionNamedSelectableStepQuantityCollectionList';
import OptionSelectableCollectionList from '../OptionSelectableCollectionList/OptionSelectableCollectionList';
import styles from './OptionCollectionList.module.scss';

// エラーメッセージ定数
const INVALID_CHARACTER_ERROR_MESSAGE =
  '利用できない文字（記号など）が含まれています。ひらがな、カタカナ、漢字、アルファベット（半角・全角）のみが利用可能です。';

export type OptionCollectionListProps = {
  optionFormData: LotteryOptionGoodsForm[];
  isEditMode?: boolean;
  step?: 'quantity' | 'name';
  onStepChange?: (step: 'quantity' | 'name') => void;
  optionDefaultSetType: LotteryOptionGoodsForm['optionDefaultSetType'];
};

function OptionCollectionList({
  optionFormData,
  isEditMode = false,
  step,
  onStepChange: _onStepChange,
  optionDefaultSetType,
}: OptionCollectionListProps) {
  const { selectedItems } = useTradeContext();
  const [inputErrors, setInputErrors] = useState<Record<string, boolean>>({});

  // 入力値のバリデーション関数
  const validateInput = (value: string): boolean => {
    // ひらがな、カタカナ、漢字、数字（全角・半角）、アルファベット（半角・全角）のみを許可
    const validPattern = /^[ぁ-んァ-ヶ一-龥々0-9０-９a-zA-Zａ-ｚＡ-Ｚ\s]*$/;
    return validPattern.test(value);
  };

  // 入力値変更時のハンドラー
  const handleInputChange = (inputKey: string, value: string) => {
    const isValid = validateInput(value);
    setInputErrors((prev) => ({
      ...prev,
      [inputKey]: !isValid,
    }));
  };
  const formattedItems = (items: Array<LotteryOptionGoodsForm>) => {
    const result = items.map((item) => {
      const stagingKey = item.label.stagingKey || '';
      const stagingInfo = createStagingInfo(stagingKey);

      const formattedItem = {
        ...item,
        ...stagingInfo,
      };
      return formattedItem;
    });

    return result;
  };

  // 期日チェック
  const isPastDeadline = (formItem: LotteryOptionGoodsForm) => {
    const now = newDate();
    const deadline = newDate(formItem.optionCutoffAt);
    return isBefore(deadline, now);
  };

  // 宛名の入力欄のプレースホルダーを取得する関数
  const getNamedInputPlaceholder = () => {
    if (optionDefaultSetType === 'name') return 'ご希望の宛名を入力してください';
    return 'ご希望の内容を入力してください';
  };

  // 「宛名選択式オプション」の数量入力UIを表示すべきかを判定する。
  // - オプション種別が `named_selectable` であること
  // - 画面ステップが `quantity` であること
  // - 申込期限前であること（期限後は数量編集させない）
  const isNamedSelectableQuantityStep = (formItem: LotteryOptionGoodsForm) => {
    if (formItem.optionInfo.type !== 'named_selectable') return false;
    if (step !== 'quantity') return false;
    return !isPastDeadline(formItem);
  };

  // optionInfo配下の配列をレンダリングする関数
  const renderOptionItems = (formItem: LotteryOptionGoodsForm, editMode: boolean) => {
    const { optionInfo } = formItem;
    const isDeadlinePassed = isPastDeadline(formItem);
    const currentStep = formItem.optionInfo.type === 'named_selectable' && isDeadlinePassed ? 'name' : step;

    // 自動入力数を表示するかどうかを判定する関数
    const shouldShowAutoInputCount = () => {
      if (!isDeadlinePassed) return false;
      if (!formItem.optionInfo.named) return false;
      return formItem.optionInfo.named.some((item) => item.isSystemInput === true);
    };

    switch (optionInfo.type) {
      case 'named':
        return (
          <OptionNamedCollectionList
            formItem={formItem}
            optionInfo={optionInfo}
            editMode={editMode}
            isDeadlinePassed={isDeadlinePassed}
            inputErrors={inputErrors}
            onInputChange={handleInputChange}
            getNamedInputPlaceholder={getNamedInputPlaceholder}
            shouldShowAutoInputCount={shouldShowAutoInputCount}
            invalidCharacterErrorMessage={INVALID_CHARACTER_ERROR_MESSAGE}
            staging={staging}
          />
        );

      case 'selectable':
        if (optionInfo.selectable && optionInfo.selectable.length > 0) {
          return (
            <OptionSelectableCollectionList
              formItem={formItem}
              selectableItems={optionInfo.selectable}
              isDeadlinePassed={isDeadlinePassed}
              selectedItems={selectedItems}
              stagingHeader={stagingHeader}
            />
          );
        }
        break;

      case 'named_selectable':
        if (!optionInfo.namedSelectable) return <></>;
        // ステップが'quantity'の場合はselectableと同じ表示
        if (currentStep === 'quantity') {
          return (
            <OptionNamedSelectableStepQuantityCollectionList
              formItem={formItem}
              namedSelectableItems={optionInfo.namedSelectable}
              isDeadlinePassed={isDeadlinePassed}
              selectedItems={selectedItems}
              inputErrors={inputErrors}
              onInputChange={handleInputChange}
              getNamedInputPlaceholder={getNamedInputPlaceholder}
              invalidCharacterErrorMessage={INVALID_CHARACTER_ERROR_MESSAGE}
              stagingHeader={stagingHeader}
            />
          );
        }

        // ステップが'name'の場合は選択した数量分の入力欄を表示
        if (currentStep === 'name') {
          return (
            <OptionNamedSelectableStepNamedCollectionList
              formItem={formItem}
              namedSelectableItems={optionInfo.namedSelectable}
              isDeadlinePassed={isDeadlinePassed}
              editMode={editMode}
              selectedItems={selectedItems}
              inputErrors={inputErrors}
              onInputChange={handleInputChange}
              getNamedInputPlaceholder={getNamedInputPlaceholder}
              invalidCharacterErrorMessage={INVALID_CHARACTER_ERROR_MESSAGE}
              staging={staging}
            />
          );
        }

        break;

      default:
        return null;
    }

    return null;
  };

  return (
    <div className={styles.optionCollectionList}>
      {formattedItems(optionFormData).map((formItem) => (
        <div
          key={formItem.id}
          className={
            formItem.optionInfo.type === 'selectable' || isNamedSelectableQuantityStep(formItem)
              ? styles.optionItemGroupQuantity
              : undefined
          }
        >
          {renderOptionItems(formItem, isEditMode)}
        </div>
      ))}
    </div>
  );
}

function getRankStyle(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1;
    case 2:
      return styles.rank2;
    default:
      return styles.defaultRankImage;
  }
}

function getRankStyleHeader(rank: number) {
  switch (rank) {
    case 1:
      return styles.rank1HeaderQuantity;
    case 2:
      return styles.rank2HeaderQuantity;
    default:
      return styles.defaultRankImageHeaderQuantity;
  }
}

function staging(formItem: LotteryOptionGoodsForm) {
  const stagingKey = formItem.label.stagingKey;

  // X連特典
  if (formItem.label.benefitMultiDrawCount && formItem.label.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitWrapper}>
        <span className={fontsStyles.montserrat}>{formItem.label.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }

  if (formItem.label.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'default'} />;
  }
  if (stagingKey) {
    const stagingInfo = createStagingInfo(stagingKey);
    return (
      <div className={`${styles.stagingRank} ${getRankStyle(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingText} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

function stagingHeader(formItem: LotteryOptionGoodsForm) {
  const stagingKey = formItem.label.stagingKey;

  // X連特典
  if (formItem.label.benefitMultiDrawCount && formItem.label.benefitMultiDrawCount > 0) {
    return (
      <div className={styles.benefitHeaderQuantityWrapper}>
        <span className={fontsStyles.montserrat}>{formItem.label.benefitMultiDrawCount}</span>
        <small>連特典</small>
      </div>
    );
  }

  if (formItem.label.isWchance) {
    // Wチャンス
    return <WChanceStaging size={'large'} />;
  }
  if (stagingKey) {
    const stagingInfo = createStagingInfo(stagingKey);
    return (
      <div className={`${styles.stagingRankHeaderQuantity} ${getRankStyleHeader(Number(stagingInfo.stagingRank))}`}>
        <p className={`${styles.stagingTextHeaderQuantity} ${fontsStyles.montserrat}`}>
          {stagingInfo.stagingText}
          <small>賞</small>
        </p>
      </div>
    );
  }

  return <></>;
}

export default OptionCollectionList;
